import { useEffect, useRef, useState, useCallback } from 'react';

// Web Speech API types are uneven across browsers; we keep this loose.
function getSR(): any {
  // @ts-expect-error: vendor-prefixed in Chromium
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export function isSpeechSupported(): boolean {
  return getSR() !== null;
}

export interface SpeechHook {
  isRecording: boolean;
  start: () => void;
  stop: () => void;
  interim: string;
}

/**
 * Hold-to-record speech transcription.
 * onFinal is called for each finalized utterance (a long press may produce
 * several if the user pauses). interim is the live in-progress transcript.
 *
 * On unsupported browsers (notably iOS Safari) the hook returns isRecording=false
 * forever and start()/stop() are no-ops; callers should also gate the mic UI on
 * isSpeechSupported() so users on those browsers see only the plain textarea.
 */
export function useSpeechRecognition(onFinal: (text: string) => void): SpeechHook {
  const [isRecording, setIsRecording] = useState(false);
  const [interim, setInterim] = useState('');
  const recognitionRef = useRef<any>(null);
  const onFinalRef = useRef(onFinal);
  onFinalRef.current = onFinal;

  useEffect(() => {
    const SR = getSR();
    if (!SR) return;
    const sr = new SR();
    sr.continuous = true;
    sr.interimResults = true;
    sr.lang = 'en-US';
    sr.onresult = (event: any) => {
      let interimText = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const r = event.results[i];
        if (r.isFinal) {
          onFinalRef.current(String(r[0].transcript).trim());
        } else {
          interimText += r[0].transcript;
        }
      }
      setInterim(interimText);
    };
    sr.onend = () => {
      setIsRecording(false);
      setInterim('');
    };
    sr.onerror = () => {
      setIsRecording(false);
      setInterim('');
    };
    recognitionRef.current = sr;
    return () => {
      try {
        sr.stop();
      } catch {
        // ignore stop on already-stopped instance
      }
      recognitionRef.current = null;
    };
  }, []);

  const start = useCallback(() => {
    const sr = recognitionRef.current;
    if (!sr || isRecording) return;
    try {
      sr.start();
      setIsRecording(true);
    } catch {
      // already started or transient error
    }
  }, [isRecording]);

  const stop = useCallback(() => {
    const sr = recognitionRef.current;
    if (!sr) return;
    try {
      sr.stop();
    } catch {
      // ignore
    }
  }, []);

  return { isRecording, start, stop, interim };
}
